import {
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { auth, firebaseConfigured } from '../lib/firebase';
import { currentAppUrl, regionDisplayName, regionFromRuntime } from '../lib/regionRuntime';

function requireFirebase() {
  if (!firebaseConfigured || !auth) {
    throw new Error('Firebase is not configured for this environment.');
  }
}

function normalizeAuthEmail(email = '') {
  return String(email || '').trim().toLowerCase();
}

function passwordResetReturnUrl() {
  return `${currentAppUrl()}/signin`;
}

export async function signInWithGoogle() {
  requireFirebase();

  const provider = new GoogleAuthProvider();
  provider.addScope('openid');
  provider.addScope('email');
  provider.addScope('profile');

  return signInWithPopup(auth, provider);
}

export async function signInWithMicrosoft() {
  requireFirebase();

  const provider = new OAuthProvider('microsoft.com');
  provider.addScope('openid');
  provider.addScope('email');
  provider.addScope('profile');

  return signInWithPopup(auth, provider);
}

export async function signInWithEmailPassword(email, password) {
  requireFirebase();

  const cleanEmail = normalizeAuthEmail(email);
  if (!cleanEmail) {
    throw Object.assign(new Error('Enter your email address.'), {
      code: 'auth/missing-email',
    });
  }

  return signInWithEmailAndPassword(auth, cleanEmail, password);
}

export async function createEmailAccount(email, password) {
  requireFirebase();

  const cleanEmail = normalizeAuthEmail(email);
  if (!cleanEmail) {
    throw Object.assign(new Error('Enter your email address.'), {
      code: 'auth/missing-email',
    });
  }

  return createUserWithEmailAndPassword(auth, cleanEmail, password);
}

function functionsRegion() {
  return regionFromRuntime('global') === 'eu' ? 'europe-west1' : 'us-central1';
}

export async function resetPassword(email) {
  requireFirebase();

  const cleanEmail = normalizeAuthEmail(email);

  if (!cleanEmail) {
    throw Object.assign(new Error('Enter your email first, then we can send a reset link.'), {
      code: 'auth/missing-email',
    });
  }

  const functions = getFunctions(auth.app, functionsRegion());
  const requestPasswordReset = httpsCallable(functions, 'nudgeRequestPasswordReset');

  const response = await requestPasswordReset({
    email: cleanEmail,
    appUrl: currentAppUrl(),
    region: regionFromRuntime('global'),
  });

  return {
    ok: true,
    email: cleanEmail,
    ...(response.data || {}),
  };
}

export async function resetPasswordEmail(email) {
  return resetPassword(email);
}

export async function sendPasswordReset(email) {
  return resetPassword(email);
}

export async function signOut() {
  if (!auth) return;
  await firebaseSignOut(auth);
}

export function friendlyAuthError(error, context = {}) {
  const code = error?.code || '';
  const mode = context.mode || '';
  const regionName = regionDisplayName(regionFromRuntime('global'));

  const messages = {
    'auth/missing-email':
      'Enter your email first, then we can send a reset link.',

    'auth/email-already-in-use':
      'This email already has a Snoooz or NudgeForMe account. Sign in instead, reset your password, or continue with Google/Outlook.',

    'auth/account-exists-with-different-credential':
      'This email is already registered with another sign-in method. Try Google, Outlook, or reset your password.',

    'auth/wrong-password':
      'That email or password does not look right. Try again, reset your password, or continue with Google/Outlook.',

    'auth/invalid-credential':
      'That email or password does not look right. Try again, reset your password, or continue with Google/Outlook.',

    'auth/user-not-found':
      mode === 'reset'
        ? `We could not find an account for this email in the ${regionName} region. Try the other region, continue with Google/Outlook, or create an account.`
        : 'We could not find an account with that email. Create an account or continue with Google/Outlook.',

    'auth/invalid-email':
      'Enter a valid email address.',

    'functions/invalid-argument':
      'Enter a valid email address.',

    'functions/resource-exhausted':
      'Too many password reset attempts. Please wait a bit and try again.',

    'functions/failed-precondition':
      'Password reset email could not be sent right now. Please contact support or try again later.',

    'functions/internal':
      'Password reset email could not be sent right now. Please try again later.',

    'auth/weak-password':
      'Use a stronger password. It should be at least 6 characters.',

    'auth/too-many-requests':
      'Too many attempts. Please wait a few minutes, then try again or reset your password.',

    'auth/popup-closed-by-user':
      'The sign-in window was closed before sign-in finished.',

    'auth/popup-blocked':
      'Your browser blocked the sign-in window. Allow popups and try again.',

    'auth/cancelled-popup-request':
      'Another sign-in window is already open. Close it and try again.',

    'auth/network-request-failed':
      'Network error. Check your connection and try again.',

    'auth/unauthorized-domain':
      'This NudgeForMe domain is not authorized for sign-in yet. Please contact support.',

    'auth/unauthorized-continue-uri':
      'Password reset is not configured for this domain yet. Add this app domain to Firebase Auth authorized domains and try again.',

    'auth/invalid-continue-uri':
      'Password reset is not configured correctly for this domain yet. Check the password reset return URL.',

    'auth/missing-continue-uri':
      'Password reset is missing a return URL. Check the reset link configuration.',

    'auth/operation-not-allowed':
      'This sign-in method is not enabled yet. Try Google, Outlook, or contact support.',

    'auth/requires-recent-login':
      'For security, please sign in again and retry.',
  };

  if (messages[code]) return messages[code];

  const rawMessage = String(error?.message || '');

  if (rawMessage.includes('Firebase:')) {
    return 'Something went wrong while signing you in. Please try again, reset your password, or continue with Google/Outlook.';
  }

  if (rawMessage.includes('auth/')) {
    return 'Something went wrong while signing you in. Please try again.';
  }

  return rawMessage || 'Could not sign in. Please try again.';
}
